import { Call02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import { useVoiceEnabled } from "providers/Auth";
import { Link } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx, formatPhone } from "shared/utils";
import { CallBackModal } from "views/calls/components/CallBackModal";
import {
  buildMissedCallUrl,
  callerLabel,
  callerUnknown,
  useMarkNotificationAsRead
} from "../helpers/call-notification";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

type MissedCallMetadata = Notification["metadata"];

// "Missed call at {number name}" — the partner-side line that was dialled,
// labelled with the property/provisioned-number name when the backend resolved
// one.
const missedCallTitle = (metadata: MissedCallMetadata): string => {
  const dialled =
    metadata.to_name?.trim() ||
    (metadata.to_number ? formatPhone(metadata.to_number) : "");
  return dialled ? `Missed call at ${dialled}` : "Missed call";
};

/**
 * Calls the caller back through the Call back pop-up (CRM-1659), starting on
 * the line they dialed. Deliberately rendered OUTSIDE the row's <Link>: an
 * interactive element inside an anchor would open the pop-up *and* navigate
 * away (marking the notification read) on a single click.
 *
 * Seeing the call log only needs the partner feature, but placing a call also
 * needs the per-user permission — hence the extra `useVoiceEnabled()` gate,
 * so the affordance disappears for inbound-only users.
 */
export const CallBackButton = ({
  metadata
}: {
  metadata: MissedCallMetadata;
}) => {
  const voiceEnabled = useVoiceEnabled();
  const [opened, setOpened] = useState(false);

  const toNumber = metadata.from_number;
  // Calling an unknown caller's "number" back would ring the AI agent.
  if (!voiceEnabled || !toNumber || callerUnknown(metadata)) return null;

  return (
    <>
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        HugeIcon={Call02Icon}
        onClick={() => setOpened(true)}
      >
        Call back
      </Button>
      <CallBackModal
        opened={opened}
        onClose={() => setOpened(false)}
        toNumber={toNumber}
        toName={metadata.from_name}
        lineNumber={metadata.to_number}
        leadId={metadata.lead_id ?? undefined}
        contactId={metadata.contact_id ?? undefined}
        customerId={metadata.customer_id ?? undefined}
      />
    </>
  );
};

interface MissedCallNotificationCardProps {
  item: Notification;
}

export const MissedCallNotificationCard = ({
  item
}: MissedCallNotificationCardProps) => {
  const { id, read_at, url, metadata, created_at } = item;
  const markAsRead = useMarkNotificationAsRead();

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const callerName = callerLabel(metadata);
  // `occurred_at` is the terminalization instant the backend also uses as the
  // row's created_at; prefer it so the timestamp can never drift from the
  // day-group heading (the digest rows do the same).
  const timeLabel = formatCalendar(metadata.occurred_at || created_at);

  return (
    <div className="flex items-stretch gap-6 bg-white p-6 hover:bg-alpha-40">
      <Link
        to={url || buildMissedCallUrl(metadata)}
        onClick={handleClick}
        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-4"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <HugeiconsIcon
            icon={Call02Icon}
            size={16}
            className="text-danger-500 shrink-0"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-danger-500">
            {missedCallTitle(metadata)}
          </p>
        </div>

        <div className="flex items-center gap-4 min-w-0">
          <span
            className={cx(
              "size-3 rounded-full shrink-0",
              read_at ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          <Avatar name={callerName} size={44} className="font-bold shrink-0" />
          <div className="min-w-0">
            <p className="text-base font-bold text-content-1000 truncate">
              {callerName}
            </p>
            <p className="text-sm text-content-400 truncate">
              Called and no one answered
            </p>
          </div>
        </div>
      </Link>

      <div className="flex shrink-0 flex-col items-end justify-between gap-4">
        <p className="text-content-300 font-medium text-sm">{timeLabel}</p>
        <CallBackButton metadata={metadata} />
      </div>
    </div>
  );
};
